import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { uniqueId } from 'lodash';
import {
  getClusterInfo,
  getClusterList,
  getEnvList,
  getHostDetail,
  getHostList,
  getK8sList,
  getLibOptions,
  getProxyOptions,
  getQuickFilterList,
  getServiceList,
} from '@/services/resourceService';

const initialState = {
  proxyOptions: [],
  libOptions: [],
  envOptions: [],
  k8sOptions: [],
  info: {},
  clusters: [],
  filters: [],
  hostDetail: {},
  hostDetailFetching: false,
  infoFetching: false,
  clusterHostsFetching: false,
  tabs: [],
  currentTab: null,
  serviceOptions: [],
};

export const fetchProxyOptions = createAsyncThunk('resource/fetchProxyOptions', async () => {
  const response = await getProxyOptions();
  return response.data;
});

export const fetchLibOptions = createAsyncThunk('resource/fetchLibOptions', async () => {
  const response = await getLibOptions();
  return response.data;
});

export const fetchEnvOptions = createAsyncThunk('resource/fetchEnvOptions', async () => {
  const response = await getEnvList();
  return response.data.envInfoList;
});

export const fetchK8sOptions = createAsyncThunk('resource/fetchK8sOptions', async () => {
  const response = await getK8sList({ startIndex: 1, pageSize: 99999 });
  return response.data.items;
});

export const fetchClusterInfo = createAsyncThunk('resource/fetchClusterInfo', async (id) => {
  const response = await getClusterInfo(id);
  return response.data;
});

export const fetchClusterList = createAsyncThunk('resource/fetchClusterList', async () => {
  const response = await getClusterList({});
  return response.data;
});

export const fetchHostDetail = createAsyncThunk('resource/fetchHostDetail', async (id) => {
  const response = await getHostDetail(id);
  return response.data;
});

export const fetchQuickFilterList = createAsyncThunk(
  'resource/fetchQuickFilterList',
  async (params) => {
    const response = await getQuickFilterList(params);
    return response.data;
  },
);

export const fetchServiceList = createAsyncThunk('resource/fetchServiceList', async () => {
  const response = await getServiceList();
  return response.data;
});

export const fetchHostsUnderCluster = createAsyncThunk(
  'resource/fetchHostsUnderCluster',
  async (params) => {
    const response = await getHostList(params);
    return {
      clusterId: params.opsClusterId,
      hosts: response.data.items,
    };
  },
);

export const resourceSlice = createSlice({
  name: 'resource',
  initialState,
  reducers: {
    addToTabs: (state, action) => {
      const id = uniqueId();
      const current = {
        ...action.payload,
        tabId: id,
      };
      state.tabs.push(current);
      state.currentTab = current;
    },
    selectTab: (state, action) => {
      const tabId = action.payload;
      state.currentTab = state.tabs.find((item) => item.tabId === tabId);
    },
    removeTab: (state, action) => {
      const tabId = action.payload;
      state.tabs = state.tabs.filter((item) => item.tabId !== tabId);
    },
    copyTab: (state, action) => {
      const current = action.payload;
      state.tabs = [...state.tabs, { ...current, tabId: uniqueId() }];
    },
    removeRight: (state, action) => {
      const tabId = action.payload;
      const { tabs } = state;
      const currentId = tabs.findIndex((item) => item.tabId === tabId);
      tabs.splice(currentId + 1);
      state.tabs = tabs;
    },
    removeOtherTabs: (state, action) => {
      const tabId = action.payload;
      state.tabs = state.tabs.filter((item) => item.tabId === tabId);
    },
    removeAllTabs: (state) => {
      state.tabs = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProxyOptions.fulfilled, (state, action) => {
        state.proxyOptions = action.payload;
      })
      .addCase(fetchLibOptions.fulfilled, (state, action) => {
        state.libOptions = action.payload;
      })
      .addCase(fetchEnvOptions.fulfilled, (state, action) => {
        state.envOptions = action.payload.map((item) => ({
          label: item.envName,
          value: item.envId,
        }));
      })
      .addCase(fetchK8sOptions.fulfilled, (state, action) => {
        state.k8sOptions = action.payload.map((item) => ({
          ...item,
          label: item.kubeName,
          value: item.id,
        }));
      })
      .addCase(fetchClusterInfo.pending, (state) => {
        state.infoFetching = true;
      })
      .addCase(fetchClusterInfo.fulfilled, (state, action) => {
        state.info = action.payload;
        state.infoFetching = false;
      })
      .addCase(fetchClusterInfo.rejected, (state) => {
        state.infoFetching = false;
      })
      .addCase(fetchClusterList.fulfilled, (state, action) => {
        state.clusters = action.payload;
      })
      .addCase(fetchHostDetail.pending, (state) => {
        state.hostDetail = {};
        state.hostDetailFetching = true;
      })
      .addCase(fetchHostDetail.fulfilled, (state, action) => {
        state.hostDetail = action.payload;
        state.hostDetailFetching = false;
      })
      .addCase(fetchHostDetail.rejected, (state) => {
        state.hostDetailFetching = false;
      })
      .addCase(fetchQuickFilterList.fulfilled, (state, action) => {
        state.filters = action.payload.map((item) => ({
          label: item.name,
          value: item.id,
        }));
      })
      .addCase(fetchHostsUnderCluster.pending, (state) => {
        state.clusterHostsFetching = true;
        state.clusters = state.clusters.map((item) => {
          delete item.hosts;
          return item;
        });
      })
      .addCase(fetchHostsUnderCluster.fulfilled, (state, action) => {
        const { clusterId, hosts } = action.payload;
        state.clusterHostsFetching = false;
        state.clusters = state.clusters.map((item) => {
          if (item.id === clusterId) {
            item.hosts = hosts;
          }
          return item;
        });
      })
      .addCase(fetchHostsUnderCluster.rejected, (state) => {
        state.clusterHostsFetching = false;
      })
      .addCase(fetchServiceList.fulfilled, (state, action) => {
        state.serviceOptions = action.payload.map((item) => ({
          value: item.envId,
          label: item.envName,
        }));
      });
  },
});

export const {
  addToTabs,
  selectTab,
  removeTab,
  copyTab,
  removeRight,
  removeOtherTabs,
  removeAllTabs,
} = resourceSlice.actions;
export default resourceSlice.reducer;
